import * as React from "react";
import { motion, useReducedMotion } from "motion/react";
import { ArrowRight, CheckCircle2, Eye, EyeOff, Loader2, MailX } from "lucide-react";
import { ApiError } from "@eduviaos/shared";
import { useQuery } from "@tanstack/react-query";
import { BrandLoader, Button, EduViaOSMark, Input, Label } from "@eduviaos/ui-kit";
import { publicApi } from "../lib/api";
import { verticalLabel, type Institution } from "../lib/institution";
import { useSession } from "../lib/sessionContext";
import { InstitutionMark } from "../components/InstitutionMark";
import { usePortalLocale } from "../lib/i18n/usePortalLocale";
import { authErrorMessage } from "../lib/apiErrorCopy";

const EASE_OUT: [number, number, number, number] = [0.23, 1, 0.32, 1];

/** Server-set floor; mirrored here so the learner learns it before submitting. */
const MIN_PASSWORD = 10;

interface InviteContext {
  tenant_slug: string;
  tenant_name: string;
  tenant_type: string;
  email: string;
  full_name: string;
  expires_at: string;
}

/**
 * Learner invite acceptance (Phase SC-4c).
 *
 * The one screen that turns a roster row into a person who can sign in.
 * Their centre added them to the roster and pressed "Invite"; this is the
 * link from that email. Setting a password activates the account, binds it
 * to their roster row server-side, and drops them straight into the portal
 * — no "now go and log in" round-trip with a password they just typed.
 *
 * The token is read anonymously first so the page can greet them by name
 * and name their centre. An unknown, expired or already-used token gets a
 * plain explanation and a route to the login form, because the most common
 * cause of a dead link is a learner who already accepted it.
 */
export function InviteScreen({
  institution,
  token,
  onSignedIn,
  loginHref,
}: {
  institution: Institution;
  token: string;
  onSignedIn: () => void;
  loginHref: string;
}) {
  const reduce = useReducedMotion();
  const { acceptInvite } = useSession();
  const { strings: s } = usePortalLocale(false);

  const ctx = useQuery<InviteContext>({
    queryKey: ["learner-invite", token],
    queryFn: async () => {
      const res = await publicApi.get<InviteContext>(
        `/onboarding/learner-invite/${encodeURIComponent(token)}`,
      );
      return res.data;
    },
    // A dead token is dead: retrying only delays the explanation.
    retry: false,
  });

  const [password, setPassword] = React.useState("");
  const [confirm, setConfirm] = React.useState("");
  const [show, setShow] = React.useState(false);
  const [pending, setPending] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const longEnough = password.length >= MIN_PASSWORD;
  const matches = password.length > 0 && password === confirm;
  const canSubmit = longEnough && matches && !pending;

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!canSubmit) return;
    setError(null);
    setPending(true);
    try {
      await acceptInvite({ token, password });
      onSignedIn();
    } catch (err) {
      if (err instanceof ApiError && (err.status === 404 || err.status === 410)) {
        setError(s.inviteExpiredError);
      } else {
        setError(authErrorMessage(err, s, s.inviteAcceptFailed));
      }
      setPending(false);
    }
  }

  if (ctx.isLoading) {
    return (
      <Shell institution={institution}>
        <div className="flex flex-col items-center justify-center gap-3 py-10">
          <BrandLoader size={40} label={s.inviteChecking} />
          <p aria-hidden="true" className="text-muted-foreground text-sm">
            {s.inviteChecking}
          </p>
        </div>
      </Shell>
    );
  }

  if (ctx.isError || !ctx.data) {
    const expired =
      ctx.error instanceof ApiError && (ctx.error.status === 404 || ctx.error.status === 410);
    return (
      <Shell institution={institution}>
        <div className="space-y-4 py-4 text-center">
          <span className="bg-muted text-muted-foreground mx-auto grid size-14 place-items-center rounded-2xl">
            <MailX className="size-7" />
          </span>
          <div className="space-y-1">
            <h2 className="font-display text-xl font-semibold">
              {expired ? "This invite has expired" : s.inviteLookupFailedTitle}
            </h2>
            <p className="text-muted-foreground text-sm">
              {expired
                ? "Invite links last a few days and can only be used once. If you already set a password, just sign in."
                : s.inviteLookupFailedBody}
            </p>
          </div>
          <Button asChild className="h-12 w-full rounded-xl text-base font-semibold">
            <a href={loginHref}>{s.loginBackToSignIn}</a>
          </Button>
        </div>
      </Shell>
    );
  }

  const firstName = ctx.data.full_name.trim().split(" ")[0] || ctx.data.full_name;

  return (
    <Shell institution={institution}>
      <div className="mb-6">
        <h2 className="font-display text-2xl font-semibold tracking-tight">
          Welcome, {firstName}.
        </h2>
        <p className="text-muted-foreground mt-1 text-sm">
          {ctx.data.tenant_name} has set up your account. Choose a password to finish.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="invite-email">Your email</Label>
          {/*
           * Read-only, not editable: the address is what the invite was
           * issued against, and letting it be changed here would silently
           * detach the login from the roster row it is bound to.
           */}
          <Input
            id="invite-email"
            value={ctx.data.email}
            readOnly
            disabled
            autoComplete="username"
            className="h-12 rounded-xl text-base"
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="invite-password">Create a password</Label>
          <div className="relative">
            <Input
              id="invite-password"
              type={show ? "text" : "password"}
              autoFocus
              autoComplete="new-password"
              placeholder={`At least ${MIN_PASSWORD} characters`}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="h-12 rounded-xl pr-12 text-base"
            />
            <button
              type="button"
              onClick={() => setShow((v) => !v)}
              aria-label={show ? "Hide password" : "Show password"}
              className="text-muted-foreground hover:text-foreground absolute inset-y-0 right-0 grid w-12 place-items-center rounded-r-xl transition-colors"
            >
              {show ? <EyeOff className="size-5" /> : <Eye className="size-5" />}
            </button>
          </div>
          <p className="text-muted-foreground flex items-center gap-1.5 text-xs">
            <CheckCircle2
              className={longEnough ? "text-primary size-3.5" : "size-3.5 opacity-40"}
              aria-hidden
            />
            {MIN_PASSWORD} characters or more
          </p>
        </div>

        <div className="space-y-2">
          <Label htmlFor="invite-confirm">Confirm password</Label>
          <Input
            id="invite-confirm"
            type={show ? "text" : "password"}
            autoComplete="new-password"
            placeholder="Type it again"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            className="h-12 rounded-xl text-base"
          />
          {confirm.length > 0 && !matches && (
            <p className="text-destructive text-xs font-medium">Those don&apos;t match yet.</p>
          )}
        </div>

        <Button
          type="submit"
          disabled={!canSubmit}
          className="h-12 w-full rounded-xl text-base font-semibold transition-transform duration-150 ease-out active:scale-[0.98]"
        >
          {pending ? (
            <>
              <Loader2 className="size-5 animate-spin" aria-hidden="true" />
              {s.inviteActivating}
            </>
          ) : (
            <>
              Start using {institution.shortName}
              <ArrowRight className="size-4" />
            </>
          )}
        </Button>
      </form>

      {error && (
        <motion.p
          initial={reduce ? false : { opacity: 0, y: -4 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.18 }}
          role="alert"
          className="bg-destructive/10 text-destructive mt-4 rounded-lg px-3 py-2 text-center text-sm font-medium"
        >
          {error}
        </motion.p>
      )}
    </Shell>
  );
}

/** Shared chrome so every state of this page keeps the same brand frame. */
function Shell({ institution, children }: { institution: Institution; children: React.ReactNode }) {
  const reduce = useReducedMotion();
  return (
    <div className="portal-aurora portal-grain bg-background text-foreground relative flex min-h-dvh flex-col items-center justify-center px-5 py-10">
      <motion.div
        initial={reduce ? false : { opacity: 0, y: 18, scale: 0.985 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.5, ease: EASE_OUT }}
        className="border-border/70 bg-card/80 w-full max-w-[26rem] rounded-[calc(var(--radius-2xl))] border p-6 shadow-[var(--shadow-lg)] backdrop-blur-xl sm:p-8"
      >
        <div className="mb-6 flex items-center gap-3">
          <InstitutionMark vertical={institution.vertical} name={institution.name} size="md" />
          <div className="leading-tight">
            <p className="font-semibold">{institution.shortName}</p>
            <p className="text-muted-foreground text-xs">{verticalLabel(institution.vertical)}</p>
          </div>
        </div>
        {children}
      </motion.div>
      <p className="text-muted-foreground mt-6 inline-flex items-center gap-1.5 text-xs">
        <EduViaOSMark className="size-3.5" />
        Powered by EduViaOS · Secure by design
      </p>
    </div>
  );
}
